import { db, generateId, now, StructureEntry, Photo, SyncQueueItem } from './database';
import { triggerImmediateUpload } from '../sync/syncEngine';
import type { Database } from '../lib/supabase';
import { apiFetchJson, isHomeserverConfigured } from '../lib/homeserver';
import { apiStorageFrom } from '../lib/storageShim';
import {
  applyPendingWrites,
  getPendingEntityIds,
  shouldFallbackToIndexedDb,
  writeThroughCache,
} from './onlineFirst';
import { mapWithConcurrency, REMOTE_READ_CONCURRENCY } from '../utils/concurrency';

type RemotePhotoRow = Database['public']['Tables']['photos']['Row'];

// Gate locale per le letture online-first via homeserver.
function isHomeserverOnline(): boolean {
  return navigator.onLine && isHomeserverConfigured();
}

export function convertRemoteToLocalStructure(remote: any): StructureEntry {
  return {
    id: remote.id,
    projectId: remote.project_id,
    floor: remote.floor,
    room: remote.room || undefined,
    intervention: remote.intervention || undefined,
    photoIds: Array.isArray(remote.photos) ? remote.photos.map((p: any) => p.id) : [],
    structures: remote.structures || [],
    toComplete: remote.to_complete || false,
    timestamp: Number(remote.timestamp),
    createdBy: remote.created_by,
    lastModified: Number(remote.last_modified),
    modifiedBy: remote.modified_by,
    version: remote.version || 1,
    synced: 1,
  };
}

export async function createStructureEntry(
  entryData: Omit<StructureEntry, 'id' | 'timestamp' | 'lastModified' | 'version' | 'synced' | 'photoIds' | 'modifiedBy'>,
  photoBlobs: Blob[]
): Promise<StructureEntry> {
  const entry: StructureEntry = {
    ...entryData,
    id: generateId(),
    timestamp: now(),
    lastModified: now(),
    modifiedBy: entryData.createdBy,
    version: 1,
    synced: 0,
    photoIds: [],
  };

  try {
    const photoIds: string[] = [];
    for (const blob of photoBlobs) {
      const photoId = generateId();
      const photo: Photo = {
        id: photoId,
        blob,
        mappingEntryId: entry.id,
        entryType: 'structure',
        metadata: {
          width: 0,
          height: 0,
          size: blob.size,
          mimeType: blob.type,
          captureTimestamp: now(),
        },
        uploaded: false,
      };

      await db.photos.add(photo);

      photoIds.push(photoId);
    }

    entry.photoIds = photoIds;
    await db.structureEntries.add(entry);

    const syncItem: SyncQueueItem = {
      id: generateId(),
      operation: 'CREATE',
      entityType: 'structure_entry',
      entityId: entry.id,
      payload: entry,
      timestamp: now(),
      retryCount: 0,
      synced: 0,
    };
    await db.syncQueue.add(syncItem);
    triggerImmediateUpload();

    return entry;
  } catch (error) {
    console.error('Failed to create structure entry:', error);
    throw error;
  }
}

export async function getStructureEntry(id: string): Promise<StructureEntry | undefined> {
  return db.structureEntries.get(id);
}

export async function getStructureEntriesForProject(
  projectId: string,
  options?: {
    floor?: string;
    sortBy?: 'timestamp' | 'floor';
    limit?: number;
  }
): Promise<StructureEntry[]> {
  if (isHomeserverOnline()) {
    try {
      const PAGE = 1000;
      const rows: Record<string, unknown>[] = [];
      let offset = 0;

      // Paginazione client-side per superare l'hardcap backend di 1000 righe per chiamata.
      while (true) {
        const params = new URLSearchParams({
          project_id: `eq.${projectId}`,
          select: '*',
          limit: String(PAGE),
          offset: String(offset),
        });

        const { data } = await apiFetchJson<{
          data: Record<string, unknown>[];
        }>(`/api/structure_entries?${params.toString()}`);
        const pageRows = data || [];

        if (pageRows.length === 0) {
          break;
        }

        rows.push(...pageRows);

        if (pageRows.length < PAGE) {
          break;
        }

        offset += PAGE;
      }

      const remoteEntries: StructureEntry[] = rows.map(convertRemoteToLocalStructure);
      const pendingIds = await getPendingEntityIds(
        'structure_entry',
        (item) => (item.payload as StructureEntry)?.projectId === projectId
      );
      const cached: StructureEntry[] = await writeThroughCache<StructureEntry>(
        remoteEntries,
        pendingIds,
        db.structureEntries
      );
      let results = await applyPendingWrites<StructureEntry>(
        cached,
        'structure_entry',
        (item) => (item.payload as StructureEntry)?.projectId === projectId
      );

      // Filtro 'floor' applicato client-side dopo overlay dei pending locali.
      if (options?.floor) {
        results = results.filter((entry) => entry.floor === options.floor);
      }

      if (options?.sortBy === 'timestamp') {
        results.sort((a, b) => b.timestamp - a.timestamp);
      } else if (options?.sortBy === 'floor') {
        results.sort((a, b) => a.floor.localeCompare(b.floor, 'it', { numeric: true }));
      } else {
        results.sort((a, b) => b.timestamp - a.timestamp);
      }

      if (options?.limit) {
        results = results.slice(0, options.limit);
      }

      return results;
    } catch (err) {
      if (!shouldFallbackToIndexedDb(err)) {
        throw err;
      }
      console.warn('[online-first] getStructureEntriesForProject fallback to IndexedDB', err);
    }
  }

  let query = db.structureEntries.where('projectId').equals(projectId);

  if (options?.floor) {
    query = query.and((entry) => entry.floor === options.floor);
  }

  let results = await query.toArray();

  if (options?.sortBy === 'timestamp') {
    results.sort((a, b) => b.timestamp - a.timestamp);
  } else if (options?.sortBy === 'floor') {
    results.sort((a, b) => a.floor.localeCompare(b.floor, 'it', { numeric: true }));
  } else {
    results.sort((a, b) => b.timestamp - a.timestamp);
  }

  if (options?.limit) {
    results = results.slice(0, options.limit);
  }

  return results;
}

export async function updateStructureEntry(
  id: string,
  updates: Partial<Omit<StructureEntry, 'id' | 'timestamp' | 'projectId'>>,
  userId: string
): Promise<StructureEntry> {
  const entry = await db.structureEntries.get(id);
  if (!entry) {
    throw new Error(`Structure entry not found: ${id}`);
  }

  const updatedEntry: StructureEntry = {
    ...entry,
    ...updates,
    lastModified: now(),
    modifiedBy: userId,
    version: entry.version + 1,
    synced: 0,
  };

  try {
    await db.structureEntries.put(updatedEntry);

    const existingSyncItem = await db.syncQueue
      .where('entityType')
      .equals('structure_entry')
      .and((item) => item.entityId === id && item.synced === 0 && item.operation !== 'DELETE')
      .first();

    if (existingSyncItem) {
      await db.syncQueue.update(existingSyncItem.id, {
        payload: updatedEntry,
        timestamp: now(),
      });
    } else {
      const syncItem: SyncQueueItem = {
        id: generateId(),
        operation: 'UPDATE',
        entityType: 'structure_entry',
        entityId: id,
        payload: updatedEntry,
        timestamp: now(),
        retryCount: 0,
        synced: 0,
      };
      await db.syncQueue.add(syncItem);
    }

    triggerImmediateUpload();

    return updatedEntry;
  } catch (error) {
    console.error('Failed to update structure entry:', error);
    throw error;
  }
}

export async function deleteStructureEntry(id: string): Promise<void> {
  try {
    const photos = await db.photos.where('mappingEntryId').equals(id).toArray();
    await db.photos.where('mappingEntryId').equals(id).delete();

    const orphanedPoints = await db.floorPlanPoints
      .where('mappingEntryId').equals(id)
      .toArray();
    const orphanedPointsByStructure = await db.floorPlanPoints
      .filter((p) => p.structureEntryId === id)
      .toArray();
    const allOrphanedPoints = [...orphanedPoints, ...orphanedPointsByStructure];

    for (const point of allOrphanedPoints) {
      await db.floorPlanPoints.delete(point.id);
      await db.syncQueue.add({
        id: generateId(),
        operation: 'DELETE',
        entityType: 'floor_plan_point',
        entityId: point.id,
        payload: { id: point.id },
        timestamp: now(),
        retryCount: 0,
        synced: 0,
      });
    }

    await db.structureEntries.delete(id);

    const syncItem: SyncQueueItem = {
      id: generateId(),
      operation: 'DELETE',
      entityType: 'structure_entry',
      entityId: id,
      payload: { id },
      timestamp: now(),
      retryCount: 0,
      synced: 0,
    };
    await db.syncQueue.add(syncItem);

    for (const photo of photos) {
      if (!photo.uploaded) continue;
      await db.syncQueue.add({
        id: generateId(),
        operation: 'DELETE',
        entityType: 'photo',
        entityId: photo.id,
        payload: {
          id: photo.id,
          mappingEntryId: photo.mappingEntryId,
          storagePath: photo.storagePath,
          thumbnailStoragePath: photo.thumbnailStoragePath,
        },
        timestamp: now(),
        retryCount: 0,
        synced: 0,
      });
    }

    triggerImmediateUpload();
  } catch (error) {
    console.error('Failed to delete structure entry:', error);
    throw error;
  }
}

const STRUCTURE_PHOTO_ID_BATCH_SIZE = 100;
const STRUCTURE_PHOTO_SIGN_BATCH_SIZE = 500;
const STRUCTURE_PHOTO_PAGE = 1000;

function chunkIds<T>(items: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
}

/**
 * Foto di più strutture in poche richieste (structure_entry_id=in.(...) a blocchi),
 * invece di una lettura + due firme per ogni struttura.
 * Come la versione singola: nessuna persistenza in IndexedDB, errori di firma
 * ignorati (resta l'URL grezzo), foto locali filtrate per entryType 'structure'.
 */
export async function getPhotosForStructures(
  structureEntryIds: string[]
): Promise<Record<string, Photo[]>> {
  const grouped: Record<string, Photo[]> = {};
  for (const id of structureEntryIds) {
    grouped[id] = [];
  }
  if (structureEntryIds.length === 0) {
    return grouped;
  }

  const loadLocal = async (): Promise<Photo[]> =>
    (await db.photos.where('mappingEntryId').anyOf(structureEntryIds).toArray())
      .filter((p) => p.entryType === 'structure');

  if (isHomeserverOnline()) {
    try {
      const batches = await mapWithConcurrency(
        chunkIds(structureEntryIds, STRUCTURE_PHOTO_ID_BATCH_SIZE),
        REMOTE_READ_CONCURRENCY,
        async (batch) => {
          const batchRows: RemotePhotoRow[] = [];
          let offset = 0;
          // Paginazione interna in caso il blocco superi le 1000 foto.
          while (true) {
            const { data } = await apiFetchJson<{ data: RemotePhotoRow[] | null }>(
              `/api/photos?structure_entry_id=in.${batch.join(',')}&select=*&limit=${STRUCTURE_PHOTO_PAGE}&offset=${offset}`
            );
            const pageRows = data || [];
            if (pageRows.length === 0) {
              break;
            }
            batchRows.push(...pageRows);
            if (pageRows.length < STRUCTURE_PHOTO_PAGE) {
              break;
            }
            offset += STRUCTURE_PHOTO_PAGE;
          }
          return batchRows;
        }
      );
      const rows = batches.flat();

      const localPhotos = await loadLocal();
      const localById = new Map(localPhotos.map((p) => [p.id, p]));

      const pendingDeletes = await db.syncQueue
        .where('entityType')
        .equals('photo')
        .and((item) => item.synced === 0 && item.operation === 'DELETE')
        .toArray();
      const pendingDeleteIds = new Set(pendingDeletes.map((item) => item.entityId));

      const filteredRows = rows.filter((row) => !pendingDeleteIds.has(row.id));

      // Genera URL firmati perché il bucket è privato.
      const fullPaths = filteredRows
        .map((row) => row.storage_path)
        .filter((p): p is string => Boolean(p));
      const thumbPaths = filteredRows
        .map((row) => row.thumbnail_storage_path)
        .filter((p): p is string => Boolean(p));

      const signedByPath = new Map<string, string>();
      const signedThumbByPath = new Map<string, string>();
      const signJobs = [
        ...chunkIds(fullPaths, STRUCTURE_PHOTO_SIGN_BATCH_SIZE).map((batch) => ({ batch, target: signedByPath })),
        ...chunkIds(thumbPaths, STRUCTURE_PHOTO_SIGN_BATCH_SIZE).map((batch) => ({ batch, target: signedThumbByPath })),
      ];
      await mapWithConcurrency(signJobs, REMOTE_READ_CONCURRENCY, async ({ batch, target }) => {
        const { data: sd } = await apiStorageFrom('photos').createSignedUrls(batch, 60 * 60);
        for (const item of sd || []) {
          if (item.path && item.signedUrl) target.set(item.path, item.signedUrl);
        }
      });

      const remoteIds = new Set<string>();
      for (const row of filteredRows) {
        // structure_entry_id esiste nello schema API ma manca dal tipo Row legacy di supabase.ts.
        const structureEntryId = (row as RemotePhotoRow & { structure_entry_id?: string }).structure_entry_id as string;
        if (!grouped[structureEntryId]) {
          continue;
        }
        remoteIds.add(row.id);
        const localPhoto = localById.get(row.id);
        grouped[structureEntryId].push({
          id: row.id,
          mappingEntryId: structureEntryId,
          entryType: 'structure' as const,
          blob: localPhoto?.blob,
          thumbnailBlob: localPhoto?.thumbnailBlob,
          metadata: row.metadata || localPhoto?.metadata || {
            width: 0, height: 0, size: 0, mimeType: 'image/jpeg', captureTimestamp: now(),
          },
          uploaded: true,
          remoteUrl: row.storage_path
            ? signedByPath.get(row.storage_path) ?? row.url ?? undefined
            : row.url ?? undefined,
          thumbnailRemoteUrl: row.thumbnail_storage_path
            ? signedThumbByPath.get(row.thumbnail_storage_path) ?? row.thumbnail_url ?? undefined
            : row.thumbnail_url ?? (row.storage_path ? signedByPath.get(row.storage_path) ?? row.url ?? undefined : row.url ?? undefined),
          storagePath: row.storage_path ?? undefined,
          thumbnailStoragePath: row.thumbnail_storage_path ?? undefined,
        });
      }

      // Unisce le foto locali non ancora sincronizzate sul remoto.
      for (const localPhoto of localPhotos) {
        if (!pendingDeleteIds.has(localPhoto.id) && !remoteIds.has(localPhoto.id) && grouped[localPhoto.mappingEntryId]) {
          grouped[localPhoto.mappingEntryId].push(localPhoto);
        }
      }

      return grouped;
    } catch (err) {
      if (!shouldFallbackToIndexedDb(err)) throw err;
      console.warn('[getPhotosForStructures] fallback to IndexedDB', err);
    }
  }

  for (const photo of await loadLocal()) {
    grouped[photo.mappingEntryId]?.push(photo);
  }
  return grouped;
}

export async function getPhotosForStructure(structureEntryId: string): Promise<Photo[]> {
  const grouped = await getPhotosForStructures([structureEntryId]);
  return grouped[structureEntryId] || [];
}

export async function addPhotosToStructure(
  structureEntryId: string,
  photoBlobs: Blob[],
  userId: string
): Promise<StructureEntry> {
  const entry = await db.structureEntries.get(structureEntryId);
  if (!entry) {
    throw new Error(`Structure entry not found: ${structureEntryId}`);
  }

  const newPhotoIds: string[] = [];
  for (const blob of photoBlobs) {
    const photoId = generateId();
    const photo: Photo = {
      id: photoId,
      blob,
      mappingEntryId: structureEntryId,
      entryType: 'structure',
      metadata: {
        width: 0,
        height: 0,
        size: blob.size,
        mimeType: blob.type,
        captureTimestamp: now(),
      },
      uploaded: false,
    };

    await db.photos.add(photo);

    newPhotoIds.push(photoId);
  }

  const updatedPhotoIds = [...(entry.photoIds || []), ...newPhotoIds];
  return updateStructureEntry(structureEntryId, { photoIds: updatedPhotoIds }, userId);
}

export async function removePhotoFromStructure(
  structureEntryId: string,
  photoId: string,
  userId: string
): Promise<StructureEntry> {
  const entry = await db.structureEntries.get(structureEntryId);
  if (!entry) {
    throw new Error(`Structure entry not found: ${structureEntryId}`);
  }

  const photo = await db.photos.get(photoId);
  await db.photos.delete(photoId);

  if (photo) {
    const syncItem: SyncQueueItem = {
      id: generateId(),
      operation: 'DELETE',
      entityType: 'photo',
      entityId: photoId,
      payload: {
        id: photo.id,
        mappingEntryId: photo.mappingEntryId,
        storagePath: photo.storagePath,
        thumbnailStoragePath: photo.thumbnailStoragePath,
      },
      timestamp: now(),
      retryCount: 0,
      synced: 0,
    };
    await db.syncQueue.add(syncItem);
    triggerImmediateUpload();
  }

  const updatedPhotoIds = (entry.photoIds || []).filter((id) => id !== photoId);
  return updateStructureEntry(structureEntryId, { photoIds: updatedPhotoIds }, userId);
}

export async function getStructureCountForProject(projectId: string): Promise<number> {
  return db.structureEntries.where('projectId').equals(projectId).count();
}
